import { createContext, useContext, useState, useEffect } from "react";
import { apiUrl } from "../lib/api";

const DEFAULT_BRANDING = {
  branded: false,
  brand_name: "Sift Careers",
  brand_logo_url: null,
  primary_color: "#2563eb",
  accent_color: "#1d4ed8",
  subdomain: null,
  partner_referral_code: null,
  logo_font_style: "default",
  card_style: "solid",
  hero_style: "default",
  loaded: false,
};

const BrandingContext = createContext(DEFAULT_BRANDING);

// The partner subdomain this page is on, or "" for Sift's own site. Read once:
// it cannot change without a full page load.
function currentSubdomain() {
  if (typeof window === "undefined") return "";
  const parts = window.location.hostname.split(".");
  const sub = parts.length >= 3 ? parts[0] : "";
  return sub === "www" ? "" : sub;
}

export function BrandingProvider({ children }) {
  /* Sift's own site (www, the bare domain, localhost) is never partner-branded,
     so it renders straight away instead of holding every page on a spinner
     until the branding call answers. That call is to an API that can be cold,
     and the homepage sat blank for a second or more on first load
     (2026-10-31). Partner subdomains still wait, since their colours and logo
     are the first thing they show. */
  const [branding, setBranding] = useState(() => (
    currentSubdomain() ? DEFAULT_BRANDING : { ...DEFAULT_BRANDING, loaded: true }
  ));

  useEffect(() => {
    const subdomain = currentSubdomain();
    if (!subdomain) return undefined;
    // Pass subdomain as query param since Azure CORS strips the Origin header
    const qs = `?subdomain=${subdomain}`;

    // Hard timeout so a slow/dead API can NEVER hang the whole app on the
    // branding spinner — the layout gates on branding.loaded, and a hanging
    // fetch never resolves or rejects. After the timeout we fall back to
    // default (unbranded) and render the app.
    const controller = new AbortController();
    let settled = false;
    const finishDefault = () => {
      if (settled) return;
      settled = true;
      setBranding({ ...DEFAULT_BRANDING, loaded: true });
    };
    const timer = setTimeout(() => {
      controller.abort();
      finishDefault();
    }, 6000);

    fetch(apiUrl(`/api/partners/branding/${qs}`), { signal: controller.signal })
      .then((r) => r.ok ? r.json() : DEFAULT_BRANDING)
      .then((data) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        setBranding({ ...data, loaded: true });

        // Inject CSS custom properties for partner theming
        if (data.branded) {
          const root = document.documentElement;
          root.style.setProperty("--brand-primary", data.primary_color);
          root.style.setProperty("--brand-accent", data.accent_color);
          // Set body background so standalone pages (outside layouts) still
          // have the branded accent color behind transparent glass cards.
          document.body.style.backgroundColor = data.accent_color;
        }

        // Inject global accent-color overrides for all branded portals
        if (data.branded) {
          const accentId = "branded-accent-overrides";
          if (!document.getElementById(accentId)) {
            const accentStyle = document.createElement("style");
            accentStyle.id = accentId;
            accentStyle.textContent = `
              /* Accent color overrides for cream-label portals */
              /* Target buttons/links/spans with white-on-black accent, exclude large containers */
              button.bg-white.text-black,
              a.bg-white.text-black,
              span.bg-white.text-black,
              [role="tab"].bg-white.text-black {
                background-color: var(--brand-primary) !important;
                color: #fff !important;
              }
              button.bg-white.text-black:hover,
              a.bg-white.text-black:hover,
              span.bg-white.text-black:hover {
                filter: brightness(0.9);
              }

              /* Blue accent buttons/links */
              .bg-blue-600, .bg-blue-500 {
                background-color: var(--brand-primary) !important;
              }
              .bg-blue-700, .bg-blue-800 {
                background-color: var(--brand-accent) !important;
              }
              .text-blue-400, .text-blue-500, .text-blue-600 {
                color: var(--brand-primary) !important;
              }
              .border-blue-700, .border-blue-600, .border-blue-500 {
                border-color: var(--brand-primary) !important;
              }

              /* Emerald/green accent buttons */
              .bg-emerald-600, .bg-emerald-700, .bg-emerald-800,
              .bg-green-600, .bg-green-700 {
                background-color: var(--brand-primary) !important;
              }
              .hover\\:bg-emerald-700:hover,
              .hover\\:bg-emerald-800:hover,
              .hover\\:bg-green-700:hover {
                background-color: var(--brand-accent) !important;
              }
              .bg-emerald-900\\/40 {
                background-color: color-mix(in srgb, var(--brand-primary) 20%, transparent) !important;
              }
              .text-emerald-300 {
                color: color-mix(in srgb, var(--brand-primary) 60%, white) !important;
              }
              .border-emerald-800\\/60 {
                border-color: var(--brand-primary) !important;
              }
            `;
            document.head.appendChild(accentStyle);
          }
        }

        // Inject global glass-morphism overrides when card_style is glass
        if (data.branded && data.card_style === "glass") {
          const styleId = "branded-glass-overrides";
          if (!document.getElementById(styleId)) {
            const style = document.createElement("style");
            style.id = styleId;
            // Use attribute selectors [class*="..."] instead of escaped class
            // selectors. Tailwind's escaping varies by version and build, but
            // the raw HTML class attribute always contains the literal string
            // e.g. 'bg-[#111]', so attribute selectors are bulletproof.
            const g = (o) =>
              `background-color:rgba(255,255,255,${o})!important;` +
              `backdrop-filter:blur(12px)!important;` +
              `-webkit-backdrop-filter:blur(12px)!important;` +
              `border-color:rgba(255,255,255,0.12)!important;`;

            // Build selectors for arbitrary hex bg classes. Keep this list in
            // sync with any new dark backgrounds introduced anywhere in the
            // app. The opacity rises with the source color's lightness so the
            // glass effect feels natural against the partner's accent backdrop.
            const hexMap = [
              // ~#22 / mid-dark (lightest of the dark backgrounds)
              ['#333',    0.14], ['#333333', 0.14], ['#2f2f2f', 0.12], ['#2a2a2a', 0.11],
              ['#222',    0.10], ['#262626', 0.10], ['#232327', 0.10],
              // ~#1c / dark
              ['#1f1f1f', 0.08], ['#1e1e1e', 0.08], ['#1c1c1c', 0.07],
              ['#1b1b1e', 0.05], ['#1a1a1a', 0.05], ['#181818', 0.06],
              ['#151515', 0.05], ['#141414', 0.04], ['#141416', 0.04],
              ['#131313', 0.04], ['#121212', 0.04],
              // ~#11 / very dark
              ['#111',    0.06],
              // ~#0f / near-black
              ['#0f0f0f', 0.03], ['#0f0f10', 0.03], ['#0e0e0e', 0.03],
              ['#0d0d0d', 0.03], ['#0d1117', 0.04],
              ['#0c0c0c', 0.03], ['#0b0b0b', 0.03], ['#0a0a0a', 0.02],
            ];
            const hexRules = hexMap
              .map(([hex, o]) => `[class*="bg-[${hex}]"]{${g(o)}}`)
              .join('\n');

            style.textContent = `
              /* ===== Glass-morphism overrides for cream-label portals ===== */
              /* Uses attribute selectors for reliable matching */

              ${hexRules}

              /* --- Tailwind named color classes --- */
              .bg-black { ${g(0.06)} }
              .bg-gray-900 { ${g(0.07)} }
              .bg-gray-950 { ${g(0.04)} }
              .bg-gray-800 { ${g(0.10)} }

              /* --- Opacity variants of bg-black --- */
              [class*="bg-black/20"] { background-color:rgba(255,255,255,0.04)!important; }
              [class*="bg-black/30"] { background-color:rgba(255,255,255,0.05)!important; }
              [class*="bg-black/40"] { background-color:rgba(255,255,255,0.06)!important; }
              [class*="bg-black/60"] {
                background-color:rgba(255,255,255,0.06)!important;
                backdrop-filter:blur(12px)!important;
                -webkit-backdrop-filter:blur(12px)!important;
              }
              [class*="bg-black/70"] {
                background-color:rgba(0,0,0,0.4)!important;
                backdrop-filter:blur(8px)!important;
                -webkit-backdrop-filter:blur(8px)!important;
              }
              [class*="bg-black/80"] {
                background-color:rgba(0,0,0,0.5)!important;
                backdrop-filter:blur(8px)!important;
                -webkit-backdrop-filter:blur(8px)!important;
              }

              /* --- Form inputs: slightly brighter glass for readability --- */
              input.bg-black, select.bg-black, textarea.bg-black,
              input[class*="bg-[#"], select[class*="bg-[#"], textarea[class*="bg-[#"] {
                background-color:rgba(255,255,255,0.08)!important;
                border-color:rgba(255,255,255,0.15)!important;
                backdrop-filter:blur(12px)!important;
                -webkit-backdrop-filter:blur(12px)!important;
              }

              /* --- Table dividers + ad-hoc borders: subtle white instead of
                 the stock dark-on-dark gray which reads "not branded" against
                 a colored backdrop --- */
              .border-gray-700, .border-gray-800, .border-gray-900,
              .border-t.border-gray-800, .border-t.border-gray-700,
              .border-b.border-gray-800, .border-b.border-gray-700,
              .divide-gray-700 > * + *, .divide-gray-800 > * + * {
                border-color:rgba(255,255,255,0.10)!important;
              }
              /* Slightly brighter for outlines on cards */
              .border.border-gray-800, .border.border-gray-700 {
                border-color:rgba(255,255,255,0.12)!important;
              }

              /* --- FullCalendar overrides --- */
              .fc {
                background-color:rgba(255,255,255,0.04)!important;
                backdrop-filter:blur(12px);
                -webkit-backdrop-filter:blur(12px);
              }
              .fc .fc-col-header-cell {
                background-color:rgba(255,255,255,0.08)!important;
                border-color:rgba(255,255,255,0.12)!important;
              }
              .fc .fc-daygrid-day,
              .fc .fc-timegrid-slot {
                background-color:rgba(255,255,255,0.03)!important;
                border-color:rgba(255,255,255,0.08)!important;
              }
              .fc .fc-day-today {
                background-color:rgba(255,255,255,0.08)!important;
                border-color:rgba(255,255,255,0.15)!important;
              }
              .fc-header-toolbar,
              .fc-toolbar,
              .fc-toolbar.fc-header-toolbar {
                background-color:rgba(255,255,255,0.06)!important;
                border-color:rgba(255,255,255,0.12)!important;
              }
              .fc .fc-button {
                background-color:rgba(255,255,255,0.1)!important;
                border-color:rgba(255,255,255,0.15)!important;
              }
              .fc .fc-button.fc-button-active {
                background-color:var(--brand-primary)!important;
                border-color:var(--brand-primary)!important;
              }
              .fc .fc-scrollgrid,
              .fc .fc-scrollgrid-section > td,
              .fc .fc-scrollgrid-section > th {
                border-color:rgba(255,255,255,0.08)!important;
              }
              .fc .fc-timegrid-divider {
                background-color:rgba(255,255,255,0.06)!important;
                border-color:rgba(255,255,255,0.08)!important;
              }
            `;
            document.head.appendChild(style);
          }
        }
      })
      .catch(() => { clearTimeout(timer); finishDefault(); });

    return () => { clearTimeout(timer); };
  }, []);

  return (
    <BrandingContext.Provider value={branding}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}

export default BrandingContext;
